import React, { useState, useRef, useEffect } from 'react';
import { 
  generateFulcrumAppJSON, 
  downloadFulcrumAppJSON 
} from './FulcrumGenerator';

import { createFulcrumApp, REGION_OPTIONS } from './FulcrumAPI';

import { 
  formAnalysisPrompt
} from './promptTemplate';

// New import for Turbo Fulcrum export
import { downloadTurboFulcrumCSV } from './TurboFulcrumExporter';

// New imports from extracted files
import { convertPDFToImages, processImageFile } from './PdfProcessor';

import { processMultiplePages, processImageWithGemini, trackMixPanelEvent } from './ApiService';


import { 
  updateFieldType, 
  updateFieldLabel, 
  deleteField,
  addChoice,
  updateChoiceLabel,
  deleteChoice,
  FieldsList
} from './FieldComponents';

import './App.css';

// Confetti component for celebration
const ConfettiOverlay = ({ show, onComplete }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onComplete, 3000); // Remove after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <div className="celebration-overlay">
      {[...Array(9)].map((_, i) => (
        <div key={i} className="confetti" />
      ))}
    </div>
  );
};

// Enhanced loading component with different animations
const EnhancedLoader = ({ type, progress }) => {
  if (type === 'pdf') {
    return (
      <div className="pdf-processing-animation">
        <div className="pdf-pages-animation">
          <div className="pdf-page-icon"></div>
          <div className="pdf-page-icon"></div>
          <div className="pdf-page-icon"></div>
          <div className="pdf-page-icon"></div>
        </div>
        <div className="processing-status">Converting PDF to images...</div>
        <div>Page {progress.currentPage} of {progress.totalPages}</div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{
              width: `${(progress.currentPage / progress.totalPages) * 100}%`
            }}
          ></div>
        </div>
        {/* Add some sparkles */}
        <div className="sparkle"></div>
        <div className="sparkle"></div>
        <div className="sparkle"></div>
        <div className="sparkle"></div>
      </div>
    );
  }

  if (type === 'ai') {
    return (
      <div className="ai-thinking-animation">

        <div className="processing-status">Analyzing form with AI...</div>
        <div className="processing-details">
          <p>Looking for form fields, labels, and input types...</p>
          <p> This may take a bit...</p>
        </div>
        <div className="loading-spinner"></div>
        {/* Add some sparkles */}
        <div className="sparkle"></div>
        <div className="sparkle"></div>
        <div className="sparkle"></div>
        <div className="sparkle"></div>
      </div>
    );
  }

  return <div className="loading-spinner"></div>;
};

// Success checkmark component
const SuccessCheckmark = () => (
  <div className="success-checkmark"></div>
);

function App() {
  const [formImage, setFormImage] = useState(null);
  const [allPageImages, setAllPageImages] = useState([]); // State for all pages
  const [formFields, setFormFields] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [formName, setFormName] = useState('Form Auto-Generated from PDF/Image');
  const [formDescription, setFormDescription] = useState('Automatically generated from form image');
  const fileInputRef = useRef(null);
  const [apiToken, setApiToken] = useState('');
  const [region, setRegion] = useState(() => localStorage.getItem('fulcrum-api-region') || 'us');
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [createdAppInfo, setCreatedAppInfo] = useState(null);
  const [processingProgress, setProcessingProgress] = useState({
    currentPage: 0,
    totalPages: 0,
    isProcessingPDF: false
  });
  // State for custom prompt
  const [customPrompt, setCustomPrompt] = useState(formAnalysisPrompt);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  
  // New state for celebrations
  const [showCelebration, setShowCelebration] = useState(false);
  const [justDetectedFields, setJustDetectedFields] = useState(false);

  // Initialize custom prompt with default on first load
  useEffect(() => {
    setCustomPrompt(formAnalysisPrompt);
  }, []);

  // Persist region selection to localStorage
  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fulcrum-api-region', region);
      }
    } catch (error) {
      // Ignore storage failures and keep the selected region in memory.
    }
  }, [region]);
  
  // Trigger celebration when fields are detected
  useEffect(() => {
    if (formFields.length > 0 && justDetectedFields) {
      setShowCelebration(true);
      setJustDetectedFields(false);
    }
  }, [formFields, justDetectedFields]);
  
  // Handle the file input change
  const handleFileInputChange = async (e) => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Check if file is PDF or image
      if (!file.type.match('application/pdf|image/png|image/jpeg')) {
        setError('Please upload a PDF or image file (PNG/JPEG)');
        trackMixPanelEvent('Instaform Form Uploaded', {
          result: 'failure',
          error: 'Please upload a PDF or image file (PNG/JPEG)'
        });
        return;
      }
      
      // If PDF, convert to images but don't process with AI yet
      if (file.type === 'application/pdf') {
        try {
          const pageImages = await convertPDFToImages(file, setProcessingProgress);
          setAllPageImages(pageImages);
          setFormImage(pageImages[0]?.imageData || null);
          trackMixPanelEvent('Instaform Form Uploaded', {
            result: 'success',
            error: null
          });
        } catch (error) {
          setError(error.message);
          trackMixPanelEvent('Instaform Form Uploaded', {
            result: 'failure',
            error: error.message
          });
        }
      } else {
        // For images, just display them without processing
        try {
          const imageData = await processImageFile(file);
          setFormImage(imageData);
          setAllPageImages([]); // Clear multi-page state for single images
          trackMixPanelEvent('Instaform Form Uploaded', {
            result: 'success',
            error: null
          });
        } catch (error) {
          setError(error.message);
          trackMixPanelEvent('Instaform Form Uploaded', {
            result: 'failure',
            error: error.message
          });
        }
      }
    }
  };

  // Start detection of form fields
  const startDetection = async () => {
    if (!formImage) {
      setError('Please upload an image or PDF first.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setJustDetectedFields(true); // Flag for celebration
    
    try {
      let processedFields;
      
      if (allPageImages.length > 0) {
        // Process PDF pages with AI
        processedFields = await processMultiplePages(allPageImages, customPrompt);
      } else {
        // Process single image with AI
        processedFields = await processImageWithGemini(formImage, customPrompt);
      }
      
      setFormFields(processedFields);
    } catch (error) {
      console.error('Error processing form:', error);
      setError(error.message || 'Failed to process the form. Please try again.');
      setJustDetectedFields(false); // Cancel celebration on error
    } finally {
      setIsProcessing(false);
    }
  };

  // Process the current image with the custom prompt
  const processWithCustomPrompt = () => {
    if (!formImage) {
      setError('Please upload an image or PDF first.');
      return;
    }
    
    startDetection();
  };

  // Field handling functions
  const handleFieldTypeChange = (index, newType) => {
    setFormFields(prevFields => updateFieldType(prevFields, index, newType));
  };

  const handleFieldLabelChange = (index, newLabel) => {
    setFormFields(prevFields => updateFieldLabel(prevFields, index, newLabel));
  };

  const handleDeleteField = (index) => {
    setFormFields(prevFields => deleteField(prevFields, index));
  };

  const handleAddChoice = (fieldIndex) => {
    setFormFields(prevFields => addChoice(prevFields, fieldIndex));
  };
  
  const handleChoiceLabelChange = (fieldIndex, choiceIndex, newLabel) => {
    setFormFields(prevFields => updateChoiceLabel(prevFields, fieldIndex, choiceIndex, newLabel));
  };
  
  const handleDeleteChoice = (fieldIndex, choiceIndex) => {
    setFormFields(prevFields => deleteChoice(prevFields, fieldIndex, choiceIndex));
  };

  // Reset custom prompt to default
  const resetPromptToDefault = () => {
    setCustomPrompt(formAnalysisPrompt);
  };

  // Toggle prompt editor visibility
  const togglePromptEditor = () => {
    setShowPromptEditor(!showPromptEditor);
  };

  const handleCreateFulcrumApp = async () => {
    if (formFields.length === 0) {
      setError('No fields detected. Cannot create Fulcrum app.');
      return;
    }

    if (!apiToken.trim()) {
      setError('API token is required to create a Fulcrum app.');
      return;
    }

    setIsCreatingApp(true);
    setError(null);
    setCreatedAppInfo(null);

    try {
      const result = await createFulcrumApp(formFields, formName, formDescription, apiToken.trim(), region);
      setCreatedAppInfo(result);
      setError(null);
    } catch (error) {
      console.error('Error creating Fulcrum app:', error);
      setError(`Failed to create Fulcrum app: ${error.message}`);
    } finally {
      setIsCreatingApp(false);
    }
  };

  // Generate and download the Fulcrum app JSON
  const handleGenerateFulcrumApp = () => {
    if (formFields.length === 0) {
      setError('No fields detected. Cannot generate Fulcrum app JSON.');
      return;
    }
    
    downloadFulcrumAppJSON(formFields, formName, formDescription);
  };
  
  // Generate and download the Turbo Fulcrum CSV
  const handleGenerateTurboFulcrumCSV = () => {
    if (formFields.length === 0) {
      setError('No fields detected. Cannot generate Turbo Fulcrum CSV.');
      return;
    }
    
    downloadTurboFulcrumCSV(formFields, formName);
  };

  // Reset and upload a new image
  const resetForm = () => {
    setFormImage(null);
    setAllPageImages([]);
    setFormFields([]);
    setError(null);
    setIsProcessing(false);
    setCreatedAppInfo(null);
    setShowCelebration(false);
    setJustDetectedFields(false);
    setProcessingProgress({
      currentPage: 0,
      totalPages: 0,
      isProcessingPDF: false
    });
  };

  return (
    <div className="App">
    <header className="App-header">
  <div className="App-title">
    <img src="/fulcrumvector.png" alt="Fulcrum Logo" className="Fulcrum-logo" />
    <span className="App-title-divider" />
    <span className="App-name">Instaform Utility</span>
  </div>
</header>


      
      <main className="App-main">
        <div className="three-column-container">
          {/* Left Column - Form Settings */}
          <div className="settings-section">
            <h2> Form Settings</h2>
            <div className="fulcrum-form-settings">
              <div className="form-group">
                <label htmlFor="formName">Form Name:</label>
                <input 
                  type="text" 
                  id="formName" 
                  value={formName} 
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="formDescription"> Description:</label>
                <textarea 
                  id="formDescription" 
                  value={formDescription} 
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows="2"
                />
              </div>
              
              {/* Prompt Editor Section */}
              <div className="form-group prompt-editor-section">
                <div className="prompt-editor-header">
                  <label>🤖 AI Prompt Settings:</label>
                  <button 
                    onClick={togglePromptEditor} 
                    className="toggle-prompt-button"
                  >
                    {showPromptEditor ? '🙈 Hide Prompt Editor' : '👀 Show Prompt Editor'}
                  </button>
                </div>
                
                {showPromptEditor && (
                  <div className="prompt-editor">
                    <textarea 
                      value={customPrompt} 
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      rows="10"
                      className="prompt-textarea"
                      placeholder="Enter your custom prompt..."
                    />
                    <div className="prompt-editor-controls">
                      <button 
                        onClick={resetPromptToDefault}
                        className="reset-prompt-button"
                      >
                        🔄 Reset to Default
                      </button>
                      <button 
                        onClick={processWithCustomPrompt}
                        className="apply-prompt-button"
                        disabled={isProcessing || !formImage}
                      >
                        {isProcessing ? '⏳ Processing...' : '✨ Apply Custom Prompt'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="apiToken">🔐 Fulcrum API Token (optional):</label>
                <input
                  type="password"
                  id="apiToken"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder="Enter your Fulcrum API token to create app directly"
                />
              </div>

              <div className="form-group">
                <label htmlFor="serverRegion">🌐 Server Region:</label>
                <select
                  id="serverRegion"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                >
                  {REGION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              {formFields.length > 0 && (
                <div className="button-group">
                  <button 
                    className="generate-button" 
                    onClick={handleGenerateFulcrumApp}
                    disabled={isCreatingApp}
                  >
                    📥 Download JSON File
                  </button>
                  
                  <button 
                    className="create-app-button" 
                    onClick={handleCreateFulcrumApp}
                    disabled={isCreatingApp || isProcessing || !apiToken.trim()}
                  >
                    {isCreatingApp ? '🔨 Building App...' : '🚀 Build App in Fulcrum'}
                  </button>
                  
                  <button 
                    className="turbo-fulcrum-button" 
                    onClick={handleGenerateTurboFulcrumCSV}
                    disabled={isCreatingApp}
                  >
                    Download Turbo Fulcrum CSV
                  </button>
                </div>
              )}

              {createdAppInfo && (
                <div className="success-message">
                  <SuccessCheckmark />
                  <h4>🎉 App Created Successfully!</h4>
                  <p>Form ID: {createdAppInfo.formId}</p>
                  <a 
                    href={createdAppInfo.formUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="app-link"
                  >
                    🌐 Open App in Fulcrum
                  </a>
                </div>
              )}
            </div>
          </div>
          
          {/* Middle Column - Input */}
          <div className="upload-section">
            <h2> Input Form</h2>
            {error && (
              <div className="error-message">
                <p>❌ {error}</p>
                <button onClick={resetForm}>🔄 Try Again</button>
              </div>
            )}
            
            {!formImage ? (
              <div className="upload-box">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept=".pdf,.png,.jpg,.jpeg"
                  id="fileInput"
                />
                <label htmlFor="fileInput" className="upload-button">
                  📎 Select Form Image or PDF
                </label>
              </div>
            ) : (
              <div className="image-preview">
                {isProcessing ? (
                  <EnhancedLoader 
                    type={processingProgress.isProcessingPDF ? 'pdf' : 'ai'}
                    progress={processingProgress}
                  />
                ) : (
                  <div>
                    <div className="image-buttons">
                      <button onClick={resetForm} className="reset-button">
                        🔄 Upload New File
                      </button>
                      <button 
                        onClick={startDetection} 
                        className="detect-button"
                        disabled={isProcessing}
                      >
                        {isProcessing ? '⏳ Processing...' : '🔍 Detect Form Fields'}
                      </button>
                    </div>
                    {allPageImages.length > 1 ? (
                      <div className="multi-page-display">
                        <h3>📚 PDF Pages ({allPageImages.length} pages)</h3>
                        {allPageImages.map((page, index) => (
                          <div key={index} className="page-preview">
                            <h4>📄 Page {page.pageNum}</h4>
                            <img 
                              src={page.imageData} 
                              alt={`Page ${page.pageNum}`} 
                              className="preview-image"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <img src={formImage} alt="Uploaded form" className="preview-image" />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Right Column - Results */}
          <div className="results-section fields-success-container">
            <ConfettiOverlay 
              show={showCelebration} 
              onComplete={() => setShowCelebration(false)} 
            />
            
            <h2>
               Results - 
              {formFields.length > 0 && (
                <span className="field-count">
                {formFields.length} Detected Field{formFields.length !== 1 ? 's' : ''}
                </span>
              )}
            </h2>
            
            <FieldsList 
              formFields={formFields}
              handlers={{
                onLabelChange: handleFieldLabelChange,
                onTypeChange: handleFieldTypeChange,
                onDelete: handleDeleteField,
                onAddChoice: handleAddChoice,
                onChoiceLabelChange: handleChoiceLabelChange,
                onDeleteChoice: handleDeleteChoice
              }}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;